import React from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

type SnackbarMessage = { message: string; severity?: AlertColor } | null;

export const SnackbarContext = React.createContext<{
  notify: (message: string, severity?: AlertColor) => void;
}>({ notify: () => {} });

export default function SnackbarProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [msg, setMsg] = React.useState<SnackbarMessage>(null);

  const notify = React.useCallback((message: string, severity: AlertColor = 'info') => {
    setMsg({ message, severity });
    setOpen(true);
  }, []);

  // notify is stable (useCallback []) so this object is created exactly once.
  // Without the memo, every snackbar open/close re-renders all context consumers.
  const contextValue = React.useMemo(() => ({ notify }), [notify]);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={3500}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={msg?.severity || 'info'}
          elevation={0}
          variant="filled"
          sx={(t) => ({
            borderRadius: `${t.ds.radius.md}px`,
            boxShadow: t.tokens.shadow.lg,
            fontWeight: 500,
            fontSize: '0.875rem',
            alignItems: 'center',
            '& .MuiAlert-icon': { padding: '7px 0', fontSize: 20 },
            '& .MuiAlert-action': { paddingTop: 0 },
          })}
        >
          {msg?.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}


